import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { FavoriteAssetsProvider } from './favorites/FavoriteAssetsContext';
import { MessagingInboxProvider } from './messaging/MessagingInboxContext';
import { useSessionSplash } from './hooks/useSessionSplash';
import { useLanguageToggleShortcut } from './hooks/useLanguageToggleShortcut';
import { LanguageProvider } from './i18n';
import { BookingCreateProvider } from './booking/BookingCreateContext';
import { ProfileBookingGateProvider } from './booking/ProfileBookingGateContext';
import { UserLocationProvider } from './context/UserLocationContext';
import { LastAppErrorProvider } from './errors/LastAppErrorContext';
import { LocalDevEnvironmentBanner } from './components/LocalDevEnvironmentBanner';
import styles from './App.module.css';
import { AssetsMenu } from './components/AssetsMenu';
import { BottomNav } from './components/BottomNav';
import { BookingMenu } from './components/BookingMenu';
import { HomeHeader } from './components/HomeHeader';
import { DesktopSiteBanner } from './components/DesktopSiteBanner';
import { ProfilePage } from './pages/ProfilePage';
import {
  AppNavigationProvider,
  GUEST_ALLOWED_VIEWS,
  useAppNavigation,
  useAppView,
} from './navigation/AppNavigationContext';
import { AuthScreen } from './pages/AuthScreen';
import { ConnectionTestPage } from './pages/ConnectionTestPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { HomePage } from './pages/HomePage';
import { MessagesPage } from './pages/MessagesPage';
import { SearchPage } from './pages/SearchPage';
import { SessionSplashScreen } from './pages/SessionSplashScreen';
import { AssistantProvider } from './assistant/AssistantProvider';
import { resolveAppEnvironment } from './appEnvironment';
import { takePendingMessagingCompose, takePendingMessagingPeerUserId } from './utils/messagingPendingPeer';

function scrollHomeToTop(el: HTMLDivElement | null, behavior: ScrollBehavior = 'smooth'): void {
  if (!el) {
    return;
  }
  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ top: 0, behavior });
  } else {
    el.scrollTop = 0;
  }
}

/** Reset scroll position for the active tab panel after bottom-nav view changes. */
function resetMainScrollPanels(el: HTMLDivElement | null): void {
  scrollHomeToTop(el, 'auto');
  if (!el) {
    return;
  }
  el.querySelectorAll<HTMLElement>('.panelEmbedded, .page').forEach((panel) => {
    panel.scrollTop = 0;
  });
}

function AppShell() {
  const view = useAppView();
  useLanguageToggleShortcut();
  useEffect(() => {
    const env = resolveAppEnvironment();
    const base = 'Placy';
    document.title = env.label ? `[${env.label}] ${base}` : base;
  }, []);
  const { openAuth, favoritesTabFocus, homeTabFocus, profileTabFocus, messagesTabFocus } =
    useAppNavigation();
  const { isLoggedIn, isAuthReady } = useAuth();
  const { splashPhase, progress } = useSessionSplash(isAuthReady);
  const [messagesPeerUserId, setMessagesPeerUserId] = useState<string | null>(null);
  const [messagesOpenCompose, setMessagesOpenCompose] = useState(false);
  const scrollMainClass =
    splashPhase === 'active'
      ? `${styles.scrollMain} ${styles.scrollMainBehindSplash}`
      : splashPhase === 'exiting'
        ? `${styles.scrollMain} ${styles.scrollMainReveal}`
        : styles.scrollMain;
  const homeScrollRef = useRef<HTMLDivElement>(null);
  const wasLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    if (messagesTabFocus < 1) {
      return;
    }
    setMessagesPeerUserId(takePendingMessagingPeerUserId());
    setMessagesOpenCompose(takePendingMessagingCompose());
  }, [messagesTabFocus]);

  const onMessagesPeerConsumed = useCallback(() => {
    setMessagesPeerUserId(null);
  }, []);

  const onMessagesComposeConsumed = useCallback(() => {
    setMessagesOpenCompose(false);
  }, []);

  useEffect(() => {
    if (!isLoggedIn && !GUEST_ALLOWED_VIEWS.includes(view)) {
      openAuth('login');
      return;
    }
    if (wasLoggedInRef.current && !isLoggedIn) {
      openAuth('login');
      resetMainScrollPanels(homeScrollRef.current);
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, openAuth, view]);

  useEffect(() => {
    resetMainScrollPanels(homeScrollRef.current);
  }, [view]);

  useEffect(() => {
    if (splashPhase === 'hidden') {
      resetMainScrollPanels(homeScrollRef.current);
    }
  }, [splashPhase]);

  useEffect(() => {
    if (homeTabFocus < 1) {
      return;
    }
    scrollHomeToTop(homeScrollRef.current, 'smooth');
  }, [homeTabFocus]);

  const splashCoversApp = splashPhase === 'active';

  const showDesktopBanner = !splashCoversApp && isLoggedIn && view !== 'connectionTest';

  const showHeader =
    !splashCoversApp &&
    (view === 'home' ||
      view === 'search' ||
      view === 'assets' ||
      view === 'bookings' ||
      view === 'favorites' ||
      view === 'messages' ||
      view === 'profile');

  const showBottomNav =
    !splashCoversApp &&
    view !== 'auth' &&
    (isLoggedIn || view === 'connectionTest');

  return (
    <div className={styles.phoneShell}>
      <div
        className={[
          styles.appFrame,
          showBottomNav ? styles.appFrameWithBottomNav : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {splashPhase !== 'active' ? <LocalDevEnvironmentBanner /> : null}
        {splashPhase !== 'hidden' ? (
          <SessionSplashScreen progress={progress} exiting={splashPhase === 'exiting'} />
        ) : null}
        {showDesktopBanner ? <DesktopSiteBanner /> : null}
        {showHeader ? <HomeHeader /> : null}
        <div ref={homeScrollRef} className={scrollMainClass}>
          {view === 'home' ? <HomePage tabFocus={homeTabFocus} /> : null}
          {view === 'auth' ? <AuthScreen /> : null}
          {view === 'search' ? <SearchPage /> : null}
          {view === 'connectionTest' ? <ConnectionTestPage /> : null}
          {view === 'assets' ? <AssetsMenu embedded pageTheme /> : null}
          {view === 'bookings' ? <BookingMenu embedded pageTheme /> : null}
          {view === 'favorites' ? <FavoritesPage tabFocus={favoritesTabFocus} /> : null}
          {view === 'messages' ? (
            <MessagesPage
              tabFocus={messagesTabFocus}
              pendingPeerUserId={messagesPeerUserId}
              openCompose={messagesOpenCompose}
              onPendingPeerConsumed={onMessagesPeerConsumed}
              onComposeConsumed={onMessagesComposeConsumed}
            />
          ) : null}
          {view === 'profile' ? <ProfilePage tabFocus={profileTabFocus} /> : null}
        </div>
        {showBottomNav ? <BottomNav activeView={view} /> : null}
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <LastAppErrorProvider>
          <BookingCreateProvider>
            <AppNavigationProvider>
              <ProfileBookingGateProvider>
                <FavoriteAssetsProvider>
                  <MessagingInboxProvider>
                    <UserLocationProvider>
                      <AssistantProvider>
                        <AppShell />
                      </AssistantProvider>
                    </UserLocationProvider>
                  </MessagingInboxProvider>
                </FavoriteAssetsProvider>
              </ProfileBookingGateProvider>
            </AppNavigationProvider>
          </BookingCreateProvider>
        </LastAppErrorProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
